/**
 * Admin · Scenarios — manual-trigger fine-tuning runner.
 *
 * Operations are 100% admin-triggered. Nothing here runs on a schedule.
 *
 *   1. Ingest      — re-loads the workbook from /app/memory/uploads/
 *   2. Run         — executes scenarios matching the chosen filter
 *   3. Capture     — promotes a completed run into the canonical baseline
 *   4. Uplift      — compares a run against the saved baseline (refuses
 *                    if no baseline exists)
 *
 * Every action shows a confirmation modal before firing because some are
 * irreversible (capture overwrites the baseline) or expensive (full library
 * runs all 18 agents and costs LLM tokens).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Play, Save, TrendingUp, Database, AlertTriangle,
  CheckCircle2, RefreshCcw, FileSpreadsheet, X, Info,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (_e) { return null; }
}

const fetchJson = async (path, token, opts = {}) => {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `${path} → ${res.status}`);
  return body;
};

// Cost model — keep in sync with backend runner. Realistic estimate of LLM
// tokens consumed per scenario (zero for skipped agents, ~$0.08 for a real
// agent invocation on Claude Sonnet via the Emergent LLM key).
const COST_PER_SCENARIO_USD = 0.08;

const Modal = ({ open, title, body, confirmLabel, onConfirm, onClose, danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="scenario-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-lg shadow-2xl">
        <div className="flex items-center gap-2 mb-3">
          {danger
            ? <AlertTriangle size={20} className="text-amber-400" />
            : <Info size={20} className="text-cyan-400" />}
          <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
        </div>
        <div className="text-sm text-slate-300 mb-5 leading-relaxed">{body}</div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm border border-slate-700"
            data-testid="modal-cancel"
          >Cancel</button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              danger
                ? 'bg-amber-600 hover:bg-amber-500 text-white'
                : 'bg-cyan-600 hover:bg-cyan-500 text-white'
            }`}
            data-testid="modal-confirm"
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

export default function AdminScenarios() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);

  const [library, setLibrary] = useState({});
  const [recentRuns, setRecentRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [lastRunResult, setLastRunResult] = useState(null);

  // Filter UI
  const [filterAgent, setFilterAgent] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterReleaseBlocker, setFilterReleaseBlocker] = useState(false);

  // Modals
  const [modalOpen, setModalOpen] = useState(null);   // 'ingest'|'run'|'capture'|'uplift'
  const [modalTargetRunId, setModalTargetRunId] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('startech_token');
    if (!t) { navigate('/'); return; }
    const claims = decodeJwt(t);
    if (!claims || claims.sub !== 'startech') { navigate('/'); return; }
    setToken(t);
  }, [navigate]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const [lib, runs] = await Promise.all([
        fetchJson('/api/admin/scenarios/library', token),
        fetchJson('/api/admin/scenarios/runs?limit=10', token),
      ]);
      setLibrary(lib);
      setRecentRuns(runs.runs || []);
    } catch (e) {
      setError(String(e.message || e));
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  // Estimated cost = matching scenario count × per-scenario cost
  const matchingCount = (() => {
    if (!library.total) return 0;
    // Best-effort estimate using the breakdowns we have. Server applies the
    // real filter; this is just a UX hint.
    let count = library.total;
    if (filterAgent && library.by_agent) count = Math.min(count, library.by_agent[filterAgent] || 0);
    if (filterPriority && library.by_priority) count = Math.min(count, library.by_priority[filterPriority] || 0);
    if (filterType && library.by_type) count = Math.min(count, library.by_type[filterType] || 0);
    if (filterReleaseBlocker) count = Math.min(count, library.release_blockers || 0);
    return count;
  })();
  const estimatedCost = (matchingCount * COST_PER_SCENARIO_USD).toFixed(2);

  const _doIngest = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const r = await fetchJson('/api/admin/scenarios/ingest', token, { method: 'POST' });
      setMessage(`Ingested ${r.inserted} scenarios from ${r.source_path?.split('/').pop()}`);
      await refresh();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); setModalOpen(null); }
  };

  const _doRun = async (runLabel) => {
    setBusy(true); setError(null); setMessage(null); setLastRunResult(null);
    try {
      const body = {
        agent: filterAgent || undefined,
        priority: filterPriority || undefined,
        type: filterType || undefined,
        release_blocker: filterReleaseBlocker || undefined,
        run_label: runLabel,
      };
      const r = await fetchJson('/api/admin/scenarios/run', token, {
        method: 'POST', body: JSON.stringify(body),
      });
      setLastRunResult(r);
      setMessage(`Run ${r.run_id} complete · ${r.passed}/${r.runnable} passed (${(r.pass_rate*100).toFixed(1)}%)`);
      await refresh();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); setModalOpen(null); }
  };

  const _doCapture = async () => {
    if (!modalTargetRunId) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      const r = await fetchJson('/api/admin/scenarios/baseline/capture', token, {
        method: 'POST', body: JSON.stringify({ run_id: modalTargetRunId, notes: '' }),
      });
      setMessage(`Baseline captured · id=${r.baseline_id} · pass-rate ${r.overall_pass_rate}`);
      await refresh();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); setModalOpen(null); setModalTargetRunId(null); }
  };

  const _doUplift = async (runId) => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const r = await fetchJson('/api/admin/scenarios/uplift', token, {
        method: 'POST', body: JSON.stringify({ run_id: runId }),
      });
      const regr = r.regressions?.length ?? 0;
      const impr = r.improvements?.length ?? 0;
      setMessage(`Uplift vs baseline ${r.baseline_id} · ${impr} improvements / ${regr} regressions`);
      setLastRunResult({ ...r, _is_uplift: true });
      await refresh();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const baseline = library.baseline;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-slate-200 p-8" data-testid="admin-scenarios">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-cyan-400 tracking-tight" data-testid="admin-scenarios-title">
              Search.Inc · Scenario Library Runner
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Manual-trigger fine-tuning · capture baseline before computing uplift · all costs admin-borne
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={refresh}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm border border-slate-700"
              data-testid="scenarios-refresh"
            ><RefreshCcw size={14} /> Refresh</button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm border border-slate-700"
              data-testid="scenarios-back"
            ><ArrowLeft size={14} /> Dashboard</button>
          </div>
        </div>

        {/* Status banner */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl border border-cyan-900/40 bg-slate-900/70" data-testid="status-library">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Library</div>
            <div className="text-3xl font-bold text-cyan-400 tabular-nums">{library.total ?? '—'}</div>
            <div className="text-xs text-slate-400 mt-1">scenarios ingested</div>
            {library.release_blockers != null && (
              <div className="text-xs text-amber-300 mt-2">
                {library.release_blockers} release blockers · {library.automated} automated
              </div>
            )}
          </div>
          <div className={`p-5 rounded-xl border ${baseline ? 'border-emerald-900 bg-emerald-950/20' : 'border-slate-800 bg-slate-900/40'}`} data-testid="status-baseline">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Baseline</div>
            {baseline ? (
              <>
                <div className="text-3xl font-bold text-emerald-400 tabular-nums">
                  {(baseline.overall_pass_rate * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  pass-rate · captured {new Date(baseline.captured_at).toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono truncate">
                  id: {baseline.baseline_id}
                </div>
              </>
            ) : (
              <>
                <div className="text-3xl font-bold text-slate-500 tabular-nums">—</div>
                <div className="text-xs text-amber-300 mt-1">
                  No baseline yet. Run scenarios + capture before computing uplift.
                </div>
              </>
            )}
          </div>
          <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/40" data-testid="status-cost">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Cost estimate (USD — Anthropic billing currency)</div>
            <div className="text-3xl font-bold text-slate-100 tabular-nums">${estimatedCost}</div>
            <div className="text-xs text-slate-400 mt-1">
              for {matchingCount} matching scenarios @ ${COST_PER_SCENARIO_USD.toFixed(2)} USD each
            </div>
          </div>
        </div>

        {/* Action panel */}
        <div className="mb-6 p-5 rounded-xl border border-slate-800 bg-slate-900/70" data-testid="action-panel">
          <h2 className="text-sm uppercase tracking-wider text-cyan-400 font-semibold mb-4">Run scenarios</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <select
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm"
              data-testid="filter-agent"
            >
              <option value="">All 18 agents</option>
              {Object.entries(library.by_agent || {}).map(([a, c]) => (
                <option key={a} value={a}>{a} ({c})</option>
              ))}
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm"
              data-testid="filter-priority"
            >
              <option value="">Any priority</option>
              {Object.entries(library.by_priority || {}).map(([p, c]) => (
                <option key={p} value={p}>{p} ({c})</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm"
              data-testid="filter-type"
            >
              <option value="">Any type</option>
              {Object.entries(library.by_type || {}).map(([t, c]) => (
                <option key={t} value={t}>{t} ({c})</option>
              ))}
            </select>
            <label className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={filterReleaseBlocker}
                onChange={(e) => setFilterReleaseBlocker(e.target.checked)}
                className="w-4 h-4"
                data-testid="filter-release-blocker"
              />
              Release blockers only
            </label>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setModalOpen('run')}
              disabled={busy || !library.total}
              className="flex items-center gap-1.5 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm font-semibold"
              data-testid="btn-run"
            >
              <Play size={14} /> Run {matchingCount > 0 ? `${matchingCount} scenarios` : 'scenarios'}
            </button>
            <button
              onClick={() => setModalOpen('ingest')}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 rounded-lg text-sm"
              data-testid="btn-ingest"
            >
              <FileSpreadsheet size={14} /> Re-ingest workbook
            </button>
          </div>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-emerald-950/30 border border-emerald-900 rounded-lg text-emerald-300 text-sm flex items-center gap-2" data-testid="success-msg">
            <CheckCircle2 size={16} /> {message}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-rose-950/40 border border-rose-900 rounded-lg text-rose-300 text-sm flex items-center gap-2" data-testid="error-msg">
            <AlertTriangle size={16} /> {error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* Last run result */}
        {lastRunResult && !lastRunResult._is_uplift && (
          <div className="mb-6 p-5 rounded-xl border border-slate-800 bg-slate-900/70" data-testid="last-run-result">
            <h3 className="text-sm uppercase tracking-wider text-cyan-400 font-semibold mb-3">
              Last run · {lastRunResult.run_id}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3 text-xs">
              {Object.entries(lastRunResult.by_status || {}).map(([k, v]) => (
                <div key={k} className="bg-slate-800/50 px-3 py-2 rounded-lg">
                  <div className="text-slate-400">{k}</div>
                  <div className="text-lg font-bold tabular-nums text-slate-100">{v}</div>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-300 mb-3">
              <strong>Pass rate:</strong> {(lastRunResult.pass_rate * 100).toFixed(1)}% ({lastRunResult.passed} / {lastRunResult.runnable})
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setModalTargetRunId(lastRunResult.run_id); setModalOpen('capture'); }}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 rounded-lg text-xs font-semibold"
                data-testid="btn-capture-baseline"
              >
                <Save size={12} /> {baseline ? 'Replace baseline' : 'Capture as baseline'}
              </button>
              {baseline && (
                <button
                  onClick={() => _doUplift(lastRunResult.run_id)}
                  disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 rounded-lg text-xs font-semibold"
                  data-testid="btn-compute-uplift"
                >
                  <TrendingUp size={12} /> Compute uplift vs baseline
                </button>
              )}
            </div>
          </div>
        )}

        {/* Uplift result */}
        {lastRunResult && lastRunResult._is_uplift && (
          <div className="mb-6 p-5 rounded-xl border border-emerald-900 bg-emerald-950/20" data-testid="uplift-result">
            <h3 className="text-sm uppercase tracking-wider text-emerald-400 font-semibold mb-3">
              Uplift vs baseline {lastRunResult.baseline_id}
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="text-xs text-slate-400">Improvements</div>
                <div className="text-2xl font-bold text-emerald-400 tabular-nums">{lastRunResult.improvements?.length ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Regressions</div>
                <div className="text-2xl font-bold text-rose-400 tabular-nums">{lastRunResult.regressions?.length ?? 0}</div>
              </div>
            </div>
            <div className="text-xs text-slate-300 space-y-1">
              {Object.entries(lastRunResult.per_agent_delta || {}).map(([a, d]) => (
                <div key={a} className="flex justify-between">
                  <span>{a}</span>
                  <span className={d.delta > 0 ? 'text-emerald-400' : d.delta < 0 ? 'text-rose-400' : 'text-slate-400'}>
                    {d.delta > 0 ? '+' : ''}{(d.delta * 100).toFixed(1)}% (now {(d.new_pass_rate * 100).toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent runs */}
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/70" data-testid="recent-runs">
          <h2 className="text-sm uppercase tracking-wider text-cyan-400 font-semibold mb-3">Recent runs</h2>
          {recentRuns.length === 0 ? (
            <p className="text-xs text-slate-500">No scenario runs yet. Click "Run scenarios" above to start.</p>
          ) : (
            <div className="space-y-2">
              {recentRuns.map((r) => (
                <div key={r.run_id} className="flex items-center justify-between p-3 bg-slate-800/40 rounded-lg text-xs">
                  <div className="font-mono text-slate-400">{r.run_id}</div>
                  <div className="text-slate-500">{r.run_label}</div>
                  <div>{r.passed} ✓ · {r.failed} ✗ · {r.errored} ⚠ · {r.skipped} −</div>
                  <div className={`font-bold ${r.pass_rate >= 0.8 ? 'text-emerald-400' : r.pass_rate >= 0.5 ? 'text-amber-400' : 'text-rose-400'}`}>
                    {(r.pass_rate * 100).toFixed(1)}%
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setModalTargetRunId(r.run_id); setModalOpen('capture'); }}
                      disabled={busy}
                      className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-[10px]"
                      data-testid={`btn-capture-${r.run_id}`}
                    >Capture</button>
                    {baseline && (
                      <button
                        onClick={() => _doUplift(r.run_id)}
                        disabled={busy}
                        className="px-2 py-1 bg-emerald-700 hover:bg-emerald-600 rounded text-[10px]"
                        data-testid={`btn-uplift-${r.run_id}`}
                      >Uplift</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-800 flex items-center gap-2 text-xs text-slate-500">
          <Database size={12} />
          <span>v3.scenarios · 540 scenarios spec · all triggers manual · admin-only · baseline required for uplift</span>
        </div>
      </div>

      <Modal
        open={modalOpen === 'ingest'}
        title="Re-ingest the scenario workbook"
        body="This will drop and re-insert all 540 scenarios from the latest workbook in /app/memory/uploads. Existing baselines and historical runs are unaffected. Use this when the workbook has been updated."
        confirmLabel="Re-ingest"
        onConfirm={_doIngest}
        onClose={() => setModalOpen(null)}
      />
      <Modal
        open={modalOpen === 'run'}
        title={`Run ${matchingCount} scenario${matchingCount === 1 ? '' : 's'}`}
        body={
          <>
            <p className="mb-2">This will execute the scenarios matching your filter. Skipped scenarios (unimplemented agents, human-review) cost nothing; only runnable scenarios consume LLM tokens.</p>
            <p className="mb-2"><strong>Estimated cost:</strong> up to ${estimatedCost} (real cost is lower — skipped scenarios are free).</p>
            <p className="text-amber-300 text-xs">Runs sequentially to avoid LLM rate-limits. Expect ~{Math.ceil(matchingCount * 2.5)}s for {matchingCount} scenarios.</p>
          </>
        }
        confirmLabel="Run now"
        onConfirm={() => _doRun(filterReleaseBlocker ? 'release_blockers' : (filterAgent ? `agent_${filterAgent}` : 'evaluation'))}
        onClose={() => setModalOpen(null)}
      />
      <Modal
        open={modalOpen === 'capture'}
        danger
        title={baseline ? 'Replace the canonical baseline' : 'Capture canonical baseline'}
        body={
          baseline
            ? `This will REPLACE the existing baseline (${baseline.baseline_id}, captured ${new Date(baseline.captured_at).toLocaleDateString()}). All future uplift calculations will compare against the new baseline. This is irreversible.`
            : 'This will freeze the selected run as the canonical baseline. All future uplift calculations compare against it. You can replace it later but not undo.'
        }
        confirmLabel={baseline ? 'Replace baseline' : 'Capture baseline'}
        onConfirm={_doCapture}
        onClose={() => { setModalOpen(null); setModalTargetRunId(null); }}
      />
    </div>
  );
}
